import {
  Button,
  cn,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@up2stay/up2promo-ui';
import { CalendarDays } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
  ComparisonMode,
  DateRangePreset,
  DateRangeValue,
} from '@/core/components/date-range-picker.types';
import {
  isComparisonSupported,
  rangeValueFromPreset,
  valuesEqual,
} from '@/core/components/date-range-picker.types';
import type { DateRangePickerLayout } from '@/core/components/date-range-picker-content';
import {
  DateRangePickerContent,
  useDateRangePickerSession,
} from '@/core/components/date-range-picker-content';
import { useLocale } from '@/core/hooks/use-locale';
import { useIsMobile } from '@/core/hooks/use-mobile';
import type { Dictionary } from '@/core/providers/dictionary-provider';
import { useDictionary } from '@/core/providers/dictionary-provider';

export type {
  ComparisonMode,
  DateRangePreset,
  DateRangeValue,
} from '@/core/components/date-range-picker.types';
export {
  computePresetRange,
  isComparisonSupported,
  rangeValueFromPreset,
} from '@/core/components/date-range-picker.types';

interface DateRangePickerProps {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  align?: 'start' | 'center' | 'end';
  className?: string;
  /**
   * `inline` — expanded in place (e.g. filter drawer). No trigger, no overlay.
   * `popover` — trigger button; popover on desktop, bottom sheet on mobile.
   */
  presentation?: 'inline' | 'popover';
  /** When inline, match parent panel styling (no extra box/padding). */
  embedded?: boolean;
  comparison?: ComparisonMode;
  onComparisonChange?: (mode: ComparisonMode) => void;
}

function useRangeLabel(value: DateRangeValue, locale: string) {
  const dict = useDictionary() as Dictionary;
  const d = dict.dateRange;

  return useMemo(() => {
    if (value.preset !== 'custom') return d.presets[value.preset];
    const fmt = new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
    if (value.from && value.to) {
      return `${fmt.format(new Date(value.from))} – ${fmt.format(new Date(value.to))}`;
    }
    if (value.from) return fmt.format(new Date(value.from));
    return d.presets.custom;
  }, [value, locale, d]);
}

function DateRangePickerBody({
  layout,
  isOpen,
  value,
  onChange,
  comparison,
  onComparisonChange,
  onClose,
  embedded,
}: {
  layout: DateRangePickerLayout;
  isOpen: boolean;
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  comparison?: ComparisonMode;
  onComparisonChange?: (mode: ComparisonMode) => void;
  onClose?: () => void;
  embedded?: boolean;
}) {
  const session = useDateRangePickerSession({
    isOpen,
    value,
    comparison,
    layout,
  });
  const wasOpenRef = useRef(isOpen);

  const commitValue = (next: DateRangeValue, options?: { close?: boolean }) => {
    session.setDraftValue(next);
    if (valuesEqual(next, value)) {
      if (options?.close) onClose?.();
      return;
    }
    onChange(next);
    if (!isComparisonSupported(next) && onComparisonChange) {
      onComparisonChange('none');
    }
    if (options?.close) onClose?.();
  };

  const flushDraft = useCallback(() => {
    if (!session.hasDraftChanges) return;

    if (!valuesEqual(session.draftValue, value)) {
      onChange(session.draftValue);
      if (!isComparisonSupported(session.draftValue) && onComparisonChange) {
        onComparisonChange('none');
      }
    }
    if (
      onComparisonChange &&
      comparison != null &&
      session.draftComparison !== (comparison ?? 'none') &&
      isComparisonSupported(session.draftValue)
    ) {
      onComparisonChange(session.draftComparison);
    }
  }, [
    comparison,
    onChange,
    onComparisonChange,
    session.draftComparison,
    session.draftValue,
    session.hasDraftChanges,
    value,
  ]);

  useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      flushDraft();
    }
    wasOpenRef.current = isOpen;
  }, [flushDraft, isOpen]);

  const handlePresetCommit = (preset: DateRangePreset) => {
    commitValue(rangeValueFromPreset(preset), { close: true });
  };

  const handleValueCommit = (next: DateRangeValue) => {
    commitValue(next, {
      close: Boolean(onClose) && next.preset === 'custom',
    });
  };

  return (
    <DateRangePickerContent
      layout={layout}
      value={value}
      draftValue={session.draftValue}
      onDraftValueChange={session.setDraftValue}
      showCalendar={session.showCalendar}
      onShowCalendarChange={session.setShowCalendar}
      draftComparison={session.draftComparison}
      onDraftComparisonChange={session.setDraftComparison}
      onComparisonChange={onComparisonChange}
      onPresetCommit={handlePresetCommit}
      onValueCommit={handleValueCommit}
      embedded={embedded}
    />
  );
}

export function DateRangePicker({
  value,
  onChange,
  align = 'start',
  className,
  presentation = 'popover',
  embedded = false,
  comparison,
  onComparisonChange,
}: DateRangePickerProps) {
  const dict = useDictionary() as Dictionary;
  const d = dict.dateRange;
  const locale = useLocale();
  const isMobile = useIsMobile();
  const label = useRangeLabel(value, locale);

  const resolvedComparison = comparison ?? 'none';
  const [open, setOpen] = useState(false);

  const showComparisonInTrigger =
    onComparisonChange != null &&
    isComparisonSupported(value) &&
    resolvedComparison !== 'none';
  const comparisonTriggerLabel =
    resolvedComparison === 'prevYear'
      ? d.comparison.vsYear
      : d.comparison.vsPeriod;

  const handleOpen = (nextOpen: boolean) => {
    setOpen(nextOpen);
  };

  if (presentation === 'inline') {
    return (
      <DateRangePickerBody
        layout="compact"
        isOpen
        value={value}
        onChange={onChange}
        comparison={comparison}
        onComparisonChange={onComparisonChange}
        embedded={embedded}
      />
    );
  }

  const trigger = (
    <Button
      type="button"
      variant="outline"
      className={cn(
        'h-9 max-w-[min(100%,22rem)] justify-start gap-2 font-normal',
        className,
      )}
      onClick={isMobile ? () => handleOpen(true) : undefined}
    >
      <CalendarDays className="h-4 w-4 shrink-0" />
      <span className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
        <span className="truncate">{label}</span>
        {showComparisonInTrigger ? (
          <span className="text-muted-foreground shrink-0 text-xs">
            · {comparisonTriggerLabel}
          </span>
        ) : null}
      </span>
    </Button>
  );

  if (isMobile) {
    return (
      <>
        {trigger}
        <Drawer open={open} onOpenChange={handleOpen}>
          <DrawerContent
            fitContent={false}
            className="h-[min(88dvh,640px)] max-h-[min(88dvh,640px)]"
          >
            {/* mx-0 px-3: title lines up with the presets below (DEV-771). */}
            <DrawerHeader className="mx-0 shrink-0 px-3 pb-2 pt-1 text-left">
              <DrawerTitle className="text-base">{d.label}</DrawerTitle>
            </DrawerHeader>
            <DateRangePickerBody
              layout="compact"
              isOpen={open}
              value={value}
              onChange={onChange}
              comparison={comparison}
              onComparisonChange={onComparisonChange}
              onClose={() => handleOpen(false)}
            />
          </DrawerContent>
        </Drawer>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align={align}
        className="w-auto p-0"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <DateRangePickerBody
          layout="desktop-popover"
          isOpen={open}
          value={value}
          onChange={onChange}
          comparison={comparison}
          onComparisonChange={onComparisonChange}
          onClose={() => handleOpen(false)}
        />
      </PopoverContent>
    </Popover>
  );
}
